import { ApiError, createApiClient, type ApiOperations, type OperationName } from '@vwt/contracts';
import { QueryClient, useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { API_BASE, fetchCredentials } from './api-base.js';

let csrfToken: string | null = null;
export const getCsrfToken = () => csrfToken;
export const setCsrfToken = (token: string | null) => {
  csrfToken = token;
};

export const api = createApiClient({
  headers: (): Record<string, string> => (csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
  baseUrl: API_BASE,
  credentials: fetchCredentials,
});

export type Args<K extends OperationName> = Parameters<typeof api.call<K>>[1];
export type Result<K extends OperationName> = ApiOperations[K]['response'];
/** The `data` payload of an operation's envelope. */
export type Data<K extends OperationName> = NonNullable<Result<K>> extends { data: infer D } ? D : never;

/** Stable, unguessable idempotency key for one user action (retries reuse it). */
export const newIdempotencyKey = () => `web-${crypto.randomUUID()}`;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5_000,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && [400, 401, 403, 404, 409, 422].includes(error.status)) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: true,
      refetchIntervalInBackground: false,
    },
  },
});

/** Visible active lists/details refresh every 15 seconds; pending jobs every 5 (Section 14.1). */
export const POLL_ACTIVE = 15_000;
export const POLL_PENDING = 5_000;

export function useApiQuery<K extends OperationName>(name: K, args: Args<K>, options: Omit<UseQueryOptions<Result<K>, ApiError>, 'queryKey' | 'queryFn'> = {}) {
  return useQuery<Result<K>, ApiError>({
    queryKey: [name, args],
    queryFn: ({ signal }) => api.call(name, { ...(args as object), signal } as Args<K>),
    ...options,
  });
}

/** Mutations invalidate related queries immediately afterwards. */
export function useApiMutation<K extends OperationName>(name: K, options: { invalidate?: OperationName[]; onSuccess?: (result: Result<K>) => void } = {}) {
  const client = useQueryClient();
  return useMutation<Result<K>, ApiError, Args<K>>({
    mutationFn: (args) => api.call(name, args),
    onSuccess: async (result) => {
      options.onSuccess?.(result);
      await Promise.all((options.invalidate ?? []).map((op) => client.invalidateQueries({ queryKey: [op] })));
    },
  });
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}
